<script lang="ts">
	/**
	 * Activity Log header. Desktop keeps the event type / room / search controls
	 * inline in the Toolbar. On mobile and tablet the inline controls are dropped
	 * and search, the filter modal trigger and refresh are published into
	 * `$listHeader` for the shared kebab, matching the other list screens. The
	 * Toolbar then only renders active filter chips.
	 */
	import { createEventDispatcher, onDestroy } from 'svelte';
	import { format } from '../../services/i18n';
	// Types
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	import type { ListHeaderConfig, ListHeaderTab } from 'src/stores/list_header';
	// Stores
	import {
		getSelectedActivityLogFilterCount,
		roomsActivityLogFilter,
		roomsActivityLogFilterInitialState,
	} from 'src/stores/ui/filters';
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { createListHeaderHandle } from 'src/stores/list_header';
	import { eventLogStickyToolbarEnabled } from 'src/stores/features/dev/event-log-sticky-toolbar';
	// Components
	import AddEditButtons from '../AddEditButtons.svelte';
	import SelectRoomFilter from '../SelectRoomFilter.svelte';
	import EventLogFilterModal from './EventLogFilterModal.svelte';
	// Design system components
	import { Select, Toolbar, Breadcrumbs, IconButton, Input, Chip } from '@Shelly/shelly-design-system';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	// Helpers
	import { capFirstChar } from '../../helpers/utilities';
	import { defaultSelectedRoom } from '../../helpers/constants';

	export let isEmptyDeviceList,
		type: 'global' | 'group' | 'device' = 'global';
	let searchTimeoutId;

	const dispatch = createEventDispatcher(),
		headerHandle = createListHeaderHandle(),
		options: SelectOptionPropsType[] = [
			{ value: 'all', title: $format('type_all') },
			{ value: 'actuators', title: $format('type_actuators') },
			{ value: 'sensors', title: $format('type_sensors') },
			{ value: 'scenes', title: $format('tab_scenes') },
			{ value: 'alarms', title: $format('tab_alarms') },
			{ value: 'lights', title: $format('type_lights', { default: 'lights' }) },
		],
		// Shortcut over the same type filter the select drives, so the two can
		// never disagree. `actuators` / `alarms` stay select-only.
		quickFilterTabs: ListHeaderTab[] = [
			{ id: 'all', label: capFirstChar($format('type_all')), icon: 'fas fa-list' },
			{ id: 'sensors', label: capFirstChar($format('type_sensors')), icon: 'fas fa-sensor-on' },
			{ id: 'scenes', label: capFirstChar($format('tab_scenes')), icon: 'fas fa-film' },
			{
				id: 'lights',
				label: capFirstChar($format('type_lights', { default: 'lights' })),
				icon: 'fas fa-lightbulb',
			},
		];

	let searchValue = '';
	let selectedTypeValue = 'all';
	let openFilterModal = false;

	$: hideInlineControls = $is_mobile || $is_tablet;
	$: selectedTypeOption = options.find((o) => o.value === selectedTypeValue) || options[0];
	$: filterCount = getSelectedActivityLogFilterCount($roomsActivityLogFilter, selectedTypeValue);
	$: notification =
		filterCount > 0 ? ({ title: filterCount, type: ComponentType.DANGER } as const) : null;

	$: activeFilterChips = [
		...($roomsActivityLogFilter.rooms || [])
			.filter((room) => room.value !== defaultSelectedRoom.value)
			.map((room) => ({
				key: `room-${room.value}`,
				title: room.title,
				onClose: resetRoomFilter,
			})),
		...(selectedTypeValue !== 'all'
			? [
					{
						key: `type-${selectedTypeValue}`,
						title: selectedTypeOption?.title || selectedTypeValue,
						onClose: resetTypeFilter,
					},
				]
			: []),
	];

	const emitSearch = (value: string) => {
		searchValue = value;
		clearTimeout(searchTimeoutId);
		searchTimeoutId = setTimeout(() => {
			dispatch('searchByName', {
				value: searchValue,
			});
		}, 250);
	};

	const onSearchByName = (e: InputEvent) => {
		emitSearch((e.target as HTMLInputElement).value);
	};

	const applyTypeFilter = (value: string) => {
		selectedTypeValue = value;
		dispatch('filterBy', {
			value,
		});
	};

	const applyRoomFilter = (value: unknown) => {
		dispatch('filterByRoom', {
			value,
		});
	};

	const onFilterByType = (e: CustomEvent) => {
		applyTypeFilter(e.detail.value);
	};

	const onFilterByRoom = (e: CustomEvent) => {
		applyRoomFilter(e.detail.value);
	};

	const onRefresh = () => {
		dispatch('refresh');
	};

	const onFilterModalApply = (e: CustomEvent<{ type: string; roomID: unknown }>) => {
		applyTypeFilter(e.detail.type);
		applyRoomFilter(e.detail.roomID);
	};

	function resetRoomFilter() {
		roomsActivityLogFilter.set({
			...roomsActivityLogFilterInitialState,
			rooms: [defaultSelectedRoom],
		});
		applyRoomFilter(defaultSelectedRoom.value);
	}

	function resetTypeFilter() {
		applyTypeFilter('all');
	}

	const resetAllFilters = () => {
		resetTypeFilter();
		resetRoomFilter();
	};

	// The quick filter tabs go into the rail on every screen size. Search, the
	// filter modal and refresh are only published on mobile/tablet, where the
	// inline toolbar controls are dropped.
	$: if ($showNewLayout && type === 'global') {
		const config: ListHeaderConfig = {
			tabs: {
				tabs: quickFilterTabs,
				activeId: selectedTypeValue,
				onSelect: applyTypeFilter,
			},
		};

		if (hideInlineControls) {
			config.menuItems = [
				{
					items: [
						{
							title: $format('label_refresh_events', {
								default: 'Refresh events',
							}),
							icon: 'fa-solid fa-rotate-right',
							disabled: isEmptyDeviceList,
							onClick: onRefresh,
						},
					],
				},
			];
			config.search = {
				value: searchValue,
				label: capFirstChar($format('search', { default: 'Search' })),
				id: 'eventlog-kebab-search-input',
				onInput: (value) => emitSearch(value),
				filter: {
					onClick: () => {
						openFilterModal = true;
					},
					disabled: isEmptyDeviceList,
					ariaLabel: $format('aria_open_filter', { default: 'Open filter' }),
					notification,
				},
			};
		}

		headerHandle.set(config);
	}

	onDestroy(() => {
		clearTimeout(searchTimeoutId);
		headerHandle.clear();
		roomsActivityLogFilter.set({ ...roomsActivityLogFilterInitialState });
	});
</script>

{#if type === 'global'}
	{#if !hideInlineControls || filterCount > 0}
		<Toolbar
			class="mb-m"
			isSticky={$eventLogStickyToolbarEnabled}
			useNewLayout={$showNewLayout}
			noBreadcrumbs={hideInlineControls}
			hideSlotBreadcrumbs={hideInlineControls}
			hideSlotLeftSide={hideInlineControls}
			hideSlotRightSide={hideInlineControls}
			hideSlotBottom={filterCount === 0}
		>
			<Breadcrumbs
				aria-label={$format('label_filter', { default: 'Filter' })}
				slot="breadcrumbs"
				links={[{ label: $format('label_filter', { default: 'Filter' }) }]}
			/>
			<svelte:fragment slot="leftSide">
				<Select
					icon="fas fa-filter"
					class="width-md-m width-sm-full"
					id="eventlog-type-select"
					aria-label={$format('label_filter_by_type', {
						default: 'Filter by type',
					})}
					value={selectedTypeOption}
					{options}
					on:change={onFilterByType}
					disabled={isEmptyDeviceList}
				/>
				<SelectRoomFilter
					disabled={isEmptyDeviceList}
					on:change={onFilterByRoom}
					appliedFilters={$roomsActivityLogFilter}
				/>
				<Input
					class="width-md-m width-sm-full"
					type="text"
					id="eventlog-search-input-1"
					aria-label={$format('label_search_events', {
						default: 'Search events',
					})}
					label={capFirstChar(
						$format('search', {
							default: 'Search',
						}),
					)}
					icon="fas fa-search"
					value={searchValue}
					on:input={onSearchByName}
					disabled={isEmptyDeviceList}
				/>
			</svelte:fragment>

			<svelte:fragment slot="rightSide">
				<IconButton
					icon="fa-solid fa-rotate-right"
					id="eventlog-refresh-btn"
					aria-label={$format('label_refresh_events', {
						default: 'Refresh events',
					})}
					on:click={onRefresh}
					disabled={isEmptyDeviceList}
				/>
				<AddEditButtons hideAddButton hideEditButton />
			</svelte:fragment>

			<svelte:fragment slot="bottom">
				{#if filterCount > 0}
					<div class="d-flex align-items-center">
						{#each activeFilterChips as chip (chip.key)}
							<Chip
								aria-label={chip.title}
								showChip
								title={chip.title}
								class="mr-s flex-shrink-0"
								on:close={chip.onClose}
							/>
						{/each}
						<IconButton
							on:click={resetAllFilters}
							icon="fas fa-xmark"
							variant="secondary"
							class="width-full"
						>
							{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
						</IconButton>
					</div>
				{/if}
			</svelte:fragment>
		</Toolbar>
	{/if}

	{#if openFilterModal}
		<EventLogFilterModal
			bind:openFilterModal
			typeOptions={options}
			{selectedTypeValue}
			on:apply={onFilterModalApply}
		/>
	{/if}
{:else}
	<Toolbar class="mb-m" isSticky>
		<Breadcrumbs
			aria-label={$format('label_filter', { default: 'Filter' })}
			slot="breadcrumbs"
			links={[{ label: capFirstChar($format('search')) }]}
		/>
		<svelte:fragment slot="leftSide">
			<SelectRoomFilter
				disabled={isEmptyDeviceList}
				on:change={onFilterByRoom}
				appliedFilters={$roomsActivityLogFilter}
			/>
			<Input
				class="mr-m width-md-s width-sm-full"
				type="text"
				id="eventlog-search-input-2"
				aria-label={$format('label_search_events', {
					default: 'Search events',
				})}
				label={capFirstChar(
					$format('search', {
						default: 'Search',
					}),
				)}
				icon="fas fa-search"
				on:input={onSearchByName}
				disabled={isEmptyDeviceList}
			/>
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			<IconButton
				icon="fas fa-sync"
				id="eventlog-sync-btn"
				aria-label={$format('label_sync_events', { default: 'Sync events' })}
				on:click={onRefresh}
				disabled={isEmptyDeviceList}
			/>
			<AddEditButtons hideAddButton hideEditButton />
		</svelte:fragment>
	</Toolbar>
{/if}
